import { isRtl } from './direction';

/**
 * Keep the installable-app metadata in step with the active language.
 *
 * None of this is React state — it lives in <head>, and it is what the OS
 * reads when someone taps "Add to Home Screen". An Arabic user was getting an
 * English name and description because these tags were hard-coded at build
 * time. Swapping them on every language change fixes that:
 *
 *   - <link rel="manifest">          the whole manifest, per language, which is
 *                                    what Android/Chrome installs from
 *   - apple-mobile-web-app-title     the iOS home-screen label (iOS reads the
 *                                    live DOM at add-time, so this works)
 *   - <title> and meta description   the browser tab and link previews
 *
 * Only `en` is served from manifest.json — the browser's default fetch — so
 * that a user who never picks a language still gets a valid manifest.
 */

const NAMES = {
  en: {
    short: 'CenterHub',
    full: 'CenterHub — Center Management',
    description:
      'Manage students, teachers, groups, lessons, attendance and surveys for your learning or therapy center.',
  },
  ar: {
    short: 'سنترهب',
    full: 'سنترهب — إدارة المراكز',
    description:
      'أدِر الطلاب والمعلمين والمجموعات والحصص والحضور والاستبيانات في مركزك التعليمي أو العلاجي.',
  },
  he: {
    short: 'סנטרהאב',
    full: 'סנטרהאב — ניהול מרכזים',
    description:
      'נהלו תלמידים, מורים, קבוצות, שיעורים, נוכחות וסקרים במרכז הלימודי או הטיפולי שלכם.',
  },
};

/** Strip any locale suffix so "ar-EG" resolves to "ar". */
const baseLang = (lang) =>
  typeof lang === 'string' ? lang.toLowerCase().split(/[-_]/)[0] : '';

function setMeta(name, content) {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

export function applyAppMetadata(lang) {
  if (typeof document === 'undefined') return;

  const key = NAMES[baseLang(lang)] ? baseLang(lang) : 'en';
  const meta = NAMES[key];

  document.title = meta.short;
  setMeta('description', meta.description);
  setMeta('apple-mobile-web-app-title', meta.short);
  setMeta('application-name', meta.short);

  const link = document.querySelector('link[rel="manifest"]');
  if (link) {
    // CRA rewrites %PUBLIC_URL% at build time; read the deployed prefix back
    // off the tag rather than assuming the app is served from the root.
    const href = link.getAttribute('href') || '/manifest.json';
    const prefix = href.replace(/manifest(\.[a-z]{2})?\.json.*$/, '');
    const next = `${prefix}${key === 'en' ? 'manifest.json' : `manifest.${key}.json`}`;
    if (href !== next) link.setAttribute('href', next);
  }
}

export { NAMES as APP_NAMES, isRtl };
